import React, {
  useState,
  useEffect,
  useRef,
  RefObject,
  ForwardRefRenderFunction,
  ReactNode,
} from "react";
import classNames from "classnames";

import Popup from "../Popup";
import PickerSlot from "./PickerSlot";
import useRefs from "../../hooks/useRefs";

export interface PickerOption {
  text: string | number;
  value: string | number;
  children?: PickerOption[];
}

export type PickerValue = string | PickerOption;
export interface PickerProps<T extends PickerValue = PickerValue> {
  visible?: boolean;
  title?: ReactNode | string;
  data: (T | T[])[];
  defaultValueData?: (string | number)[];
  className?: string;
  style?: React.CSSProperties;
  mode?: "normal" | "3D";
  swipeDuration?: number | string;
  onConfirm?: (selectedValue: T[], index: number) => void;
  onCancel?: () => void;
  onClose?: () => void;
  onCloseUpdate?: (
    selectedValue: (string | number)[],
    list: PickerOption[],
    pickerRef: RefObject<HTMLDivElement>
  ) => void;
  onChange?: (
    index: number,
    value: (string | number)[],
    selectedOptions: PickerValue[]
  ) => void;
}

const InternalPicker: ForwardRefRenderFunction<HTMLDivElement, PickerProps> = (
  props,
  ref
) => {
  const {
    visible = false,
    title,
    data = [],
    defaultValueData,
    onConfirm,
    onClose,
    onCloseUpdate,
    onChange,
    className,
    style,
    mode,
    swipeDuration,
    ...rest
  } = props;

  const [chooseValueData, setchooseValueData] = useState<
    Array<string | number>
  >([]); // 选择的数据的 value 值, 每一条数据的 value 值
  const [columnIndex, setcolumnIndex] = useState<number>(0); // 选中列
  const pickerRef = useRef<any>(null);
  const [refs, setRefs] = useRefs();
  const [columnsList, setColumnsList] = useState<PickerOption[][]>([]); // 格式化后每一列的数据

  const isConfirmEvent = useRef(false);

  // 默认值修改
  useEffect(() => {
    if (
      defaultValueData &&
      defaultValueData.length !== 0 &&
      defaultValueData.toString() !== chooseValueData.toString()
    ) {
      const data = [...defaultValueData];
      setchooseValueData(data);
      setColumnsList(normalListData() as PickerOption[][]);
    }
  }, [defaultValueData]);

  // 选中值进行修改
  useEffect(() => {
    onChange && onChange(columnIndex, chooseValueData, selectedOptions());
    if (isConfirmEvent.current) {
      isConfirmEvent.current = false;
      onConfirm && onConfirm(selectedOptions(), columnIndex);
    }
  }, [chooseValueData]);

  // 列表格式修改
  useEffect(() => {
    init();
  }, [data]);

  const closeActionSheet: React.MouseEventHandler<HTMLSpanElement> = e => {
    e.stopPropagation();
    onClose && onClose();
    // onCloseUpdate &&
    //   onCloseUpdate(chooseValueData, selectedOptions(), pickerRef);
  };
  // 点击确定
  const confirm: React.MouseEventHandler<HTMLSpanElement> = e => {
    e.stopPropagation();
    let movings = false;
    refs.forEach((_ref: any) => {
      if (_ref.moving) movings = true;
      _ref.stopMomentum();
    });

    if (movings) {
      isConfirmEvent.current = true;
    } else {
      onConfirm && onConfirm(selectedOptions(), columnIndex);
    }

    onClose && onClose();

    setTimeout(() => {
      isConfirmEvent.current = false;
    }, 0);
  };

  const selectedOptions = () => {
    const optins: PickerValue[] = [];
    columnsList.forEach((column: PickerValue[], index: number) => {
      let currOptions: Array<PickerValue> = [];
      currOptions = column.filter(
        item =>
          (typeof item === "object" ? item.value : item) ===
          chooseValueData[index]
      );
      if (currOptions[0]) {
        optins.push(currOptions[0]);
      } else {
        column[0] && optins.push(column[0]);
      }

      return column;
    });

    return optins;
  };

  // 选择每一列的数据
  const chooseItem = (option: PickerValue, columnIndex: number) => {
    if (option && Object.keys(option).length) {
      // 移动后是否与之前有差异
      if (
        chooseValueData[columnIndex] !==
        (typeof option === "string" ? option : option.value)
      ) {
        if (columnsType() === "cascade") {
          chooseValueData[columnIndex] =
            typeof option === "object" ? option.value : option || "";
          setchooseValueData([...chooseValueData]);

          let index = columnIndex;
          let cursor = option;
          while (
            typeof cursor === "object" &&
            cursor &&
            cursor.children &&
            cursor.children[0]
          ) {
            chooseValueData[index + 1] = cursor.children[0].value;
            setchooseValueData([...chooseValueData]);
            index++;
            cursor = cursor.children[0];
          }
          // 当前改变列的下一列 children 值为空
          if (typeof cursor === "object" && cursor && cursor.children) {
            chooseValueData[index + 1] = "";
            setchooseValueData([...chooseValueData]);
          }

          setColumnsList(normalListData() as PickerOption[][]);
        } else {
          setchooseValueData(data => {
            const cdata = [...data];
            cdata[columnIndex] =
              (typeof option === "string" ? option : option.value) || "";
            return cdata;
          });
        }
        setcolumnIndex(columnIndex);
      }
    }
  };
  // 传入的数据格式化
  const normalListData = () => {
    const type = columnsType();
    switch (type) {
      case "multiple":
        return data;
      case "cascade":
        // 级联数据处理
        return formatCascade(data as PickerOption[], chooseValueData);
      default:
        return [data];
    }
  };
  // 每一列的类型
  const columnsType = () => {
    const firstColumn = data[0];
    if (firstColumn) {
      if (Array.isArray(firstColumn)) {
        return "multiple";
      }
      if (typeof firstColumn === "object" && "children" in firstColumn) {
        return "cascade";
      }
    }
    return "single";
  };

  // 级联数据格式化
  const formatCascade = (
    columns: PickerOption[],
    defaultValues: (number | string)[]
  ) => {
    const formatted: PickerOption[][] = [];
    let cursor: PickerOption = {
      text: "",
      value: "",
      children: columns,
    };

    let columnIndex = 0;

    while (cursor && cursor.children) {
      const options: PickerOption[] = cursor.children;
      const value = defaultValues[columnIndex];
      let index = options.findIndex(columnItem => columnItem.value === value);
      if (index === -1) index = 0;
      cursor = cursor.children[index];

      columnIndex++;
      formatted.push(options);
    }

    return formatted;
  };

  const init = () => {
    const data: (string | number)[] = [];

    const normalData: PickerOption[][] = normalListData() as PickerOption[][];

    setColumnsList(normalData);

    normalData.length > 0 &&
      normalData.forEach(item => {
        const perItem = item[0];
        perItem &&
          data.push(typeof perItem === "string" ? perItem : perItem.value);
      });

    if (!defaultValueData && chooseValueData.length === 0) {
      setchooseValueData([...data]);
    }
  };

  const renderToolbar = () => {
    return (
      <div className="xh-picker-header">
        <span
          className="xh-picker-header-button picker-cancel"
          onClick={closeActionSheet}
        >
          取消
        </span>
        <div className="xh-picker-header-title">{title || ""}</div>
        <span
          className="xh-picker-header-button picker-confirm"
          onClick={confirm}
        >
          确定
        </span>
      </div>
    );
  };
  return (
    <Popup visible={visible}>
      <div
        className={classNames("xh-picker", className)}
        style={style}
        ref={ref}
        {...rest}
      >
        {renderToolbar()}
        <div className="xh-picker-panel" ref={pickerRef}>
          {columnsList?.map((item, index) => {
            return (
              <PickerSlot
                ref={setRefs(index)}
                defaultValue={chooseValueData?.[index]}
                data={item}
                mode={mode}
                onConfirm={chooseItem}
                swipeDuration={swipeDuration}
                key={index}
                keyIndex={index}
              />
            );
          })}
        </div>
      </div>
    </Popup>
  );
};

const Picker = React.forwardRef<unknown, Partial<PickerProps>>(InternalPicker);
export default Picker;
